/**
 * One vault item. Masked by default; the plaintext only exists in memory after
 * an explicit reveal, and the parent owns that state so it can wipe all of it at
 * once. This component never fetches or stores a secret itself.
 */
import { useEffect, useState } from 'react';
import type { VaultItem } from '../../shared/types';
import type { CopyKind } from './VaultPage';

interface Props {
  item: VaultItem;
  /** Plaintext, only while revealed. null = masked. */
  secret: string | null;
  revealing: boolean;
  /** Seconds until the auto-mask; null when masked. */
  secondsLeft: number | null;
  copied: CopyKind | null;
  encryptionReady: boolean;
  onReveal: () => void;
  onHide: () => void;
  onCopy: (kind: CopyKind, value: string) => void;
  onEdit: () => void;
  onDelete: () => void;
}

const BTN =
  'inline-flex h-9 items-center gap-1.5 rounded-full px-3 text-xs font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-surface focus-visible:outline-none';
const ICON_BTN =
  'grid h-11 w-11 place-items-center rounded-full text-ink-faint transition-colors hover:bg-surface-sunk hover:text-ink focus-visible:ring-2 focus-visible:ring-sage focus-visible:outline-none';

export default function VaultItemCard({
  item,
  secret,
  revealing,
  secondsLeft,
  copied,
  encryptionReady,
  onReveal,
  onHide,
  onCopy,
  onEdit,
  onDelete,
}: Props) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // Never leave a delete confirmation armed and forgotten.
  useEffect(() => {
    if (!confirmingDelete) return;
    const t = window.setTimeout(() => setConfirmingDelete(false), 8000);
    return () => window.clearTimeout(t);
  }, [confirmingDelete]);

  const href = item.url
    ? /^https?:\/\//i.test(item.url)
      ? item.url
      : `https://${item.url}`
    : null;

  return (
    <article className="flex flex-col rounded-card border border-border bg-surface p-4">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-medium text-ink">{item.title}</h3>
          {item.username && (
            <div className="mt-0.5 flex items-center gap-1">
              <span className="truncate text-sm text-ink-soft" title={item.username}>
                {item.username}
              </span>
              <button
                type="button"
                onClick={() => onCopy('username', item.username as string)}
                className="shrink-0 rounded-full p-1.5 text-ink-faint hover:bg-surface-sunk hover:text-ink focus-visible:ring-2 focus-visible:ring-sage focus-visible:outline-none"
                aria-label={`Copy username for ${item.title}`}
              >
                <CopyIcon className="h-3.5 w-3.5" />
              </button>
              {copied === 'username' && <span className="text-xs text-sage">Copied</span>}
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center">
          <button type="button" onClick={onEdit} className={ICON_BTN} aria-label={`Edit ${item.title}`}>
            <PencilIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setConfirmingDelete(true)}
            className={ICON_BTN}
            aria-label={`Delete ${item.title}`}
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* ---- the secret ---- */}
      <div className="mt-3">
        {!item.has_secret ? (
          <p className="flex items-center gap-1.5 text-sm text-ink-faint">
            <DotsIcon className="h-4 w-4" />
            No secret saved —{' '}
            <button
              type="button"
              onClick={onEdit}
              className="rounded font-medium text-sage underline underline-offset-2 hover:opacity-80 focus-visible:ring-2 focus-visible:ring-sage focus-visible:outline-none"
            >
              add one
            </button>
          </p>
        ) : secret === null ? (
          <div className="flex items-center justify-between gap-2 rounded-xl bg-surface-sunk px-3 py-2">
            <span
              className="font-mono text-lg leading-none tracking-[0.2em] text-ink-faint select-none"
              aria-hidden="true"
            >
              ••••••••••
            </span>
            <span className="sr-only">Secret hidden</span>
            <button
              type="button"
              onClick={onReveal}
              disabled={revealing || !encryptionReady}
              className={`${BTN} border border-border bg-surface text-ink hover:bg-canvas focus-visible:ring-sage disabled:cursor-not-allowed disabled:opacity-50`}
              aria-label={`Reveal the secret for ${item.title}`}
            >
              <EyeIcon className="h-4 w-4" />
              {revealing ? 'Revealing…' : 'Reveal'}
            </button>
          </div>
        ) : (
          <div className="rounded-xl border border-sage/30 bg-sage-soft px-3 py-2">
            <p className="font-mono text-sm break-all text-ink" data-testid="vault-secret">
              {secret}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => onCopy('secret', secret)}
                className={`${BTN} bg-sage text-white hover:opacity-90 focus-visible:ring-sage`}
              >
                <CopyIcon className="h-3.5 w-3.5" />
                {copied === 'secret' ? 'Copied' : 'Copy'}
              </button>
              <button
                type="button"
                onClick={onHide}
                className={`${BTN} border border-border bg-surface text-ink hover:bg-canvas focus-visible:ring-sage`}
              >
                <EyeOffIcon className="h-4 w-4" />
                Hide
              </button>
              <span className="text-xs text-ink-soft">
                {copied === 'secret'
                  ? 'Copied — clipboard clears in a minute'
                  : secondsLeft !== null
                    ? `Hides in ${secondsLeft}s`
                    : ''}
              </span>
            </div>
          </div>
        )}

        {item.has_secret && !encryptionReady && secret === null && (
          <p className="mt-1.5 text-xs text-ink-faint">
            Can't be revealed until the encryption key is configured.
          </p>
        )}
      </div>

      {/* ---- meta ---- */}
      {(href || item.notes) && (
        <div className="mt-3 space-y-1.5 border-t border-border pt-3">
          {href && (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-dusk underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:ring-dusk focus-visible:outline-none"
            >
              <LinkIcon className="h-3.5 w-3.5" />
              <span className="truncate">{item.url}</span>
            </a>
          )}
          {item.notes && <p className="text-sm whitespace-pre-wrap text-ink-soft">{item.notes}</p>}
        </div>
      )}

      {/* ---- delete confirmation ---- */}
      {/* Soft delete: the row keeps its ciphertext at active = 0, so this is
          recoverable from the database — but there is no in-app undo, so it
          still gets a confirmation and the copy says exactly that. */}
      {confirmingDelete && (
        <div
          role="alertdialog"
          aria-label={`Confirm deleting ${item.title}`}
          className="mt-3 rounded-xl border border-clay/40 bg-clay-soft p-3"
        >
          <p className="text-sm text-ink">
            Remove <span className="font-medium">{item.title}</span>
            {item.has_secret ? ' and its saved secret' : ''} from the vault? There's no undo button —
            getting it back means going into the database.
          </p>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={() => {
                setConfirmingDelete(false);
                onDelete();
              }}
              className={`${BTN} bg-clay text-white hover:opacity-90 focus-visible:ring-clay`}
            >
              <TrashIcon className="h-3.5 w-3.5" />
              Remove it
            </button>
            <button
              type="button"
              onClick={() => setConfirmingDelete(false)}
              className={`${BTN} border border-border bg-surface text-ink hover:bg-canvas focus-visible:ring-sage`}
              autoFocus
            >
              Keep it
            </button>
          </div>
        </div>
      )}
    </article>
  );
}

// ---------------------------------------------------------------- icons

function EyeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M4 4l16 16" strokeLinecap="round" />
      <path d="M9.9 5.9A9.6 9.6 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a17 17 0 0 1-3.2 3.9M6.6 7.9A16.7 16.7 0 0 0 2.5 12S6 18.5 12 18.5c1 0 1.9-.2 2.7-.5" />
    </svg>
  );
}

function CopyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15V5a2 2 0 0 1 2-2h8" />
    </svg>
  );
}

function PencilIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M4 20h4L19 9a2.1 2.1 0 0 0-3-3L5 17v3Z" strokeLinejoin="round" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13" strokeLinejoin="round" />
    </svg>
  );
}

function LinkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M10 13a4 4 0 0 0 6 .5l2-2a4 4 0 0 0-5.7-5.7l-1 1" strokeLinecap="round" />
      <path d="M14 11a4 4 0 0 0-6-.5l-2 2A4 4 0 0 0 11.7 18l1-1" strokeLinecap="round" />
    </svg>
  );
}

function DotsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="6" cy="12" r="1.5" />
      <circle cx="12" cy="12" r="1.5" />
      <circle cx="18" cy="12" r="1.5" />
    </svg>
  );
}
